import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import {
  Typography,
  TextField,
  Button,
  Snackbar,
  RichText,
} from '@homeviews/shared-ui'
import type { Review } from '@homeviews/models'
import type { ITheme } from '@homeviews/styles'
import {
  useCreateReviewReplyMutation,
  useUpdateReviewReplyMutation,
  useApproveReviewReplyMutation,
  getUser,
} from '@homeviews/store'
import { HAS_PERMISSION } from '@homeviews/users'
import { mixpanelTrack } from '@homeviews/seo'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {},
    form: {
      display: 'flex',
      flexDirection: 'column',
    },
    formFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignSelf: 'flex-end',
      '& button': {
        width: 'auto',
      },
      '& button:first-child': {
        marginRight: 6,
      },
    },
    publishButton: {
      marginLeft: 6,
      width: 'auto',
    },
    pendingText: {
      fontWeight: 500,
      marginTop: 15,
      marginBottom: 20,
    },
    replyContent: {},

    replyContentBox: {
      border: `1px solid ${theme.colors.primary}`,
      borderRadius: 5,
      boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
      padding: 14,
    },
    replyAuthor: {
      fontWeight: 500,
      marginTop: 14,
      lineHeight: '22px',
      letterSpacing: '-0.22px',
      color: theme.colors.brandDarkGrey,
      marginBottom: 6,
      '& span': {
        fontWeight: 400,
        color: theme.colors.grey500,
      },
    },
    approveButtonsWrapper: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 20,
    },
  }),
)

interface IProps {
  handleCancelForm: () => void
  review: Review
}

interface IState {
  replyText: string
  jobTitle: string
  displayName: string
  sending: boolean
  showReply: boolean
  showEditForm: boolean
  showWriteReply: boolean
  approved: boolean
}

const initialState = {
  replyText: '',
  jobTitle: '',
  displayName: '',
  sending: false,
  showReply: false,
  showEditForm: false,
  showWriteReply: false,
  approved: false,
}

const ReplyReviewForm: React.FC<IProps> = ({ review, handleCancelForm }) => {
  const classes = useStyles()
  const user = useSelector(getUser)

  const [state, setState] = useState<IState>(initialState)
  const [isApprovedByDeveloper, setIsApprovedByDeveloper] =
    useState<boolean>(false)
  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message: string
    status?: 'success' | 'error'
  }>({
    open: false,
    message: '',
    status: undefined,
  })

  useEffect(() => {
    let jobTitle = ''
    let displayName = ''

    if (review?.reply?.id) {
      jobTitle = review?.reply?.job_title || ''
      displayName = review?.reply?.display_name || ''
    } else {
      jobTitle = user?.job_title
      displayName =
        user?.first_name && user?.last_name
          ? `${user?.first_name}  ${user?.last_name}`
          : ''
    }

    setState({
      ...state,
      showWriteReply:
        !review?.reply?.id && (CAN_REPLY || CAN_REPLY_WITHOUT_MODERATION)
          ? true
          : false,
      showReply: review?.reply?.id ? true : false,
      replyText: review?.reply?.reply_content || '',
      jobTitle,
      displayName,
    })
  }, [review?.id, review?.reply])

  const [createReviewReply, { isLoading }] = useCreateReviewReplyMutation()
  const [updateReviewReply, { isLoading: isUpdating }] =
    useUpdateReviewReplyMutation()
  const [approveReviewReply, { isLoading: isApproving }] =
    useApproveReviewReplyMutation()

  const CAN_REPLY_WITHOUT_MODERATION = HAS_PERMISSION(
    user,
    'REPLY_TO_REVIEWS_WITHOUT_MODERATION',
    'development',
    {
      devId: review?.development_id,
    },
  )

  const CAN_REPLY = HAS_PERMISSION(user, 'REPLY_TO_REVIEWS', 'development', {
    devId: review?.development_id,
  })

  const handleFieldChange =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setState({
        ...state,
        [name]: value,
      })
    }

  const onSubmitToReview = async (e: React.FormEvent) => {
    e.preventDefault()

    setState({
      ...state,
      sending: true,
    })

    const payload = {
      data: {
        review_id: review?.id,
        reply_content: state.replyText,
        display_name: state.displayName || '',
        job_title: state.jobTitle || '',
        developer_id: user?.developer_id,
      },
    }

    try {
      await createReviewReply(payload).unwrap()

      mixpanelTrack('REVIEWS_RESPONSE', {
        'Display Name': state.displayName || '',
        'Response type': CAN_REPLY_WITHOUT_MODERATION
          ? 'Needs Moderation'
          : 'No Moderation',
      })

      setState({
        ...state,
        showEditForm: false,
        showWriteReply: false,
        showReply: true,
      })

      setSnackbarVisible({
        open: true,
        message: 'Response added',
      })
    } catch (err) {
      console.log(err)
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    } finally {
      setState({
        ...state,
        sending: false,
      })
    }
  }

  const onUpdateReply = async (e: React.FormEvent) => {
    e.preventDefault()

    const payload = {
      data: {
        reply_content: state.replyText,
        display_name: state.displayName || '',
        job_title: state.jobTitle || '',
        status: review?.reply?.status
          ? review.reply.status.toLowerCase()
          : 'draft',
      },
      id: review?.reply?.id,
    }

    try {
      await updateReviewReply(payload).unwrap()

      if (CAN_REPLY_WITHOUT_MODERATION || CAN_REPLY) {
        setState({
          ...state,
          showEditForm: false,
          showWriteReply: false,
          showReply: true,
        })
      }
    } catch (err) {
      console.log(err)
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    }
  }

  const onApproveReply = async (e: React.FormEvent) => {
    e.preventDefault()

    const payload = {
      id: review?.reply?.id,
    }

    try {
      await approveReviewReply(payload).unwrap()
      setIsApprovedByDeveloper(true)

      setState({
        ...state,
        showEditForm: false,
        showWriteReply: false,
        showReply: true,
        approved: true,
      })
    } catch (err) {
      console.log(err)
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    }
  }

  const handleSnackbarClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarVisible({
      open: false,
      message: snackbarVisible?.message,
      status: snackbarVisible?.status,
    })
  }

  if (
    !HAS_PERMISSION(user, 'REPLY_TO_REVIEWS', 'development', {
      devId: review?.development_id,
    })
  ) {
    return null
  }

  const isFormValid = () => {
    if (state.displayName && state.replyText && state.jobTitle) {
      return true
    }
    return false
  }

  // Leave for debugging purposes
  console.log(CAN_REPLY_WITHOUT_MODERATION, review, state, user)

  return (
    <div className={classes.root}>
      {state.showWriteReply && (
        <form className={classes.form}>
          <TextField
            value={state.replyText}
            label="Reply*"
            data-id="t-reply"
            id="reply"
            name="reply"
            onChange={handleFieldChange('replyText')}
            fullWidth
            multiline
            rows={5}
          />
          <TextField
            value={state.displayName}
            label="Display Name*"
            data-id="t-displayName"
            id="displayName"
            name="displayName"
            onChange={handleFieldChange('displayName')}
            fullWidth
          />
          <TextField
            value={state.jobTitle}
            label="Job Title*"
            data-id="t-job_title"
            id="job_title"
            name="job_title"
            onChange={handleFieldChange('jobTitle')}
            fullWidth
          />
          <div className={classes.formFooter}>
            <Button
              variant="outlined"
              customVariant="outlinedPrimary"
              fullWidth
              onClick={() => handleCancelForm()}
            >
              Cancel without replying
            </Button>
            {!review?.reply?.id && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                customColor="primary"
                isLoading={isLoading}
                disabled={!isFormValid()}
                onClick={onSubmitToReview}
              >
                {CAN_REPLY_WITHOUT_MODERATION
                  ? 'Submit response for publishing'
                  : 'Submit for review'}
              </Button>
            )}

            {review?.reply?.id &&
              (CAN_REPLY_WITHOUT_MODERATION ||
                review?.reply?.status === 'Draft') && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  type="submit"
                  customColor="primary"
                  isLoading={isUpdating}
                  disabled={!isFormValid()}
                  onClick={onUpdateReply}
                >
                  Save
                </Button>
              )}
          </div>
        </form>
      )}

      {review?.reply?.id && state.showReply && (
        <>
          {review?.reply?.display_name && (
            <Typography className={classes.replyAuthor}>
              Manager Response by
              <span>
                {' '}
                {review?.reply?.display_name}
                {state?.jobTitle ? `, ${state?.jobTitle}` : ''}
              </span>
            </Typography>
          )}
          <div className={classes.replyContentBox}>
            <RichText
              content={state?.replyText}
              className={classes.replyContent}
            />
          </div>
          {review?.reply?.status !== 'Pending' && !state.approved && (
            <div className={classes.approveButtonsWrapper}>
              {review?.reply?.id &&
              CAN_REPLY &&
              review?.reply?.status === 'Draft' ? (
                <>
                  <Button
                    variant="outlined"
                    customColor="dark"
                    onClick={() => {
                      setState({
                        ...state,
                        showWriteReply: true,
                        showReply: false,
                      })
                    }}
                  >
                    Edit
                  </Button>
                  {CAN_REPLY_WITHOUT_MODERATION && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      customColor="primary"
                      isLoading={isApproving}
                      onClick={onApproveReply}
                      className={classes.publishButton}
                      disabled={!isFormValid()}
                    >
                      Submit response for publishing
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="outlined"
                  customColor="dark"
                  onClick={() => {
                    handleCancelForm()
                  }}
                >
                  Close
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {review?.reply?.status === 'Pending' && (
        <Typography className={classes.pendingText} color="primary">
          This reply is pending HomeViews approval
        </Typography>
      )}
      {review?.reply?.status === 'Draft' && (
        <Typography className={classes.pendingText} color="primary">
          {isApprovedByDeveloper
            ? 'This reply is pending HomeViews approval'
            : 'This reply is pending developer approval'}
        </Typography>
      )}

      <Snackbar
        open={snackbarVisible?.open}
        onClose={(e, reason) => {
          handleSnackbarClose(e, reason)
          handleCancelForm()
        }}
        alertMessage={snackbarVisible?.message}
        status={snackbarVisible?.status}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      />
    </div>
  )
}

export default ReplyReviewForm
